import React, { useEffect, useState, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { ColDef } from 'ag-grid-community';
import GelatoDataGrid from '../components/DataGrid/GelatoDataGrid';
import apiService from '../services/api';
import {
  PageContainer,
  PageHeader,
  Alert,
  Badge,
  Button,
  StatCard,
  StatGrid,
  StatValue,
  useToast,
} from '../components/ui';

// Varianti delle schede riepilogo: colore del valore e sfondo tenue
const StatSuccess = styled(StatCard)`
  ${StatValue} {
    color: var(--success);
  }
`;

const StatWarn = styled(StatCard)`
  background: var(--warning-soft);

  ${StatValue} {
    color: var(--warning);
  }
`;

const StatDone = styled(StatCard)`
  background: var(--success-soft);

  ${StatValue} {
    color: var(--success);
  }
`;

const WeekArrow = styled.span`
  color: var(--gray-400);
  margin: 0 var(--space-sm);
`;

const CellMeta = styled.div`
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  margin-top: 3px;
`;

const AmountText = styled.span`
  font-weight: var(--font-weight-bold);
  color: var(--success);
  font-size: 1.05rem;
`;

const EmptyDash = styled.span`
  color: var(--gray-400);
`;

const WarnButton = styled(Button)`
  background: var(--warning-soft);
  color: var(--warning);
  border-color: transparent;

  &:hover:not(:disabled) {
    background: var(--warning-soft);
    color: var(--warning);
  }
`;

interface Versamento {
  week_start: string;
  week_end: string;
  totale_contanti: number;
  importo_preparato: number | null;
  preparato: boolean | null;
  preparato_da_username: string | null;
  preparato_at: string | null;
  prelevato: boolean | null;
  prelevato_da_username: string | null;
  prelevato_at: string | null;
}

const VersamentoCassaPage: React.FC = () => {
  const toast = useToast();
  const [data, setData] = useState<Versamento[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flagging, setFlagging] = useState<string | null>(null);

  // Ref per accesso aggiornato dalla cellRenderer senza ricreazione colDef
  const gridApiRef = useRef<any>(null);
  const ctxRef = useRef<any>({});

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getKiaraVersamenti();
      setData(res.data || []);
    } catch (e: any) {
      setError(e.message || 'Errore caricamento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setFlag = async (weekStart: string, tipo: 'preparato' | 'prelevato', importo?: number) => {
    const key = `${weekStart}-${tipo}`;
    setFlagging(key);
    try {
      await apiService.setKiaraVersamentoFlag(weekStart, tipo, importo);
      await load();
    } catch (e: any) {
      toast.error(e.response?.data?.error || e.message || 'Errore');
    } finally {
      setFlagging(null);
    }
  };

  // Forza refresh celle AG Grid quando cambia flagging
  useEffect(() => {
    gridApiRef.current?.refreshCells({ force: true });
  }, [flagging]);

  const fmtDateTime = (d: string | null) => {
    if (!d) return '-';
    return new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  // Aggiorna il contesto ref ad ogni render (dopo tutte le dichiarazioni)
  ctxRef.current = { flagging, setFlag, fmtDateTime, toast };

  const colsVersamenti = useMemo<ColDef[]>(() => [
    {
      headerName: 'Settimana',
      field: 'week_start',
      minWidth: 220,
      cellRenderer: (p: any) => (
        <span>
          <strong>{new Date(p.data.week_start).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}</strong>
          <WeekArrow>→</WeekArrow>
          <strong>{new Date(p.data.week_end).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}</strong>
        </span>
      ),
    },
    {
      headerName: 'Contanti (Teorico)',
      field: 'totale_contanti',
      type: 'numericColumn',
      minWidth: 140,
      cellStyle: { color: 'var(--success)', fontWeight: 600, fontSize: '1rem' },
      valueFormatter: p => `€ ${Number(p.value || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
    {
      headerName: 'Importo Preparato',
      field: 'importo_preparato',
      minWidth: 160,
      cellRenderer: (p: any) => {
        const { flagging: fl, setFlag: sf, toast: tst } = ctxRef.current;
        const val = p.data.importo_preparato;

        if (val !== null && val !== undefined) {
          return (
            <AmountText>
              € {Number(val).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </AmountText>
          );
        }

        if (p.data.preparato) {
          const key = `${p.data.week_start}-preparato`;
          const handleInputMissingImporto = () => {
            const rawVal = window.prompt("Inserisci l'importo effettivamente preparato per questa settimana:");
            if (rawVal === null) return;
            const parsed = parseFloat(rawVal.replace(',', '.'));
            if (isNaN(parsed) || parsed < 0) {
              tst.error('Inserisci un importo numerico valido superiore o uguale a 0');
              return;
            }
            sf(p.data.week_start, 'preparato', parsed);
          };

          return (
            <WarnButton $size="sm" onClick={handleInputMissingImporto} disabled={!!fl}>
              {fl === key ? 'Salvataggio...' : 'Inserisci Importo'}
            </WarnButton>
          );
        }

        return <EmptyDash>-</EmptyDash>;
      }
    },
    {
      headerName: 'Preparato',
      field: 'preparato',
      minWidth: 200,
      sortable: false,
      filter: false,
      cellRenderer: (p: any) => {
        const { flagging: fl, setFlag: sf, fmtDateTime: fdt, toast: tst } = ctxRef.current;
        const key = `${p.data.week_start}-preparato`;
        if (p.data.preparato) {
          return (
            <div>
              <Badge $variant="success">Preparato</Badge>
              <CellMeta>
                {p.data.preparato_da_username || '—'} · {fdt(p.data.preparato_at)}
              </CellMeta>
            </div>
          );
        }

        const handleSegnaPreparato = () => {
          const rawVal = window.prompt("Inserisci l'importo effettivamente preparato per questa settimana:");
          if (rawVal === null) return;
          const parsed = parseFloat(rawVal.replace(',', '.'));
          if (isNaN(parsed) || parsed < 0) {
            tst.error('Inserisci un importo numerico valido superiore o uguale a 0');
            return;
          }
          sf(p.data.week_start, 'preparato', parsed);
        };

        return (
          <Button $variant="success" $size="sm" onClick={handleSegnaPreparato} disabled={!!fl}>
            {fl === key ? 'Salvataggio...' : 'Segna Preparato'}
          </Button>
        );
      },
    },
    {
      headerName: 'Prelevato',
      field: 'prelevato',
      minWidth: 200,
      sortable: false,
      filter: false,
      cellRenderer: (p: any) => {
        const { flagging: fl, setFlag: sf, fmtDateTime: fdt } = ctxRef.current;
        const key = `${p.data.week_start}-prelevato`;
        if (p.data.prelevato) {
          return (
            <div>
              <Badge $variant="info">Prelevato</Badge>
              <CellMeta>
                {p.data.prelevato_da_username || '—'} · {fdt(p.data.prelevato_at)}
              </CellMeta>
            </div>
          );
        }
        return (
          <Button $size="sm" onClick={() => sf(p.data.week_start, 'prelevato')} disabled={!!fl}>
            {fl === key ? 'Salvataggio...' : 'Segna Prelevato'}
          </Button>
        );
      },
    },
  ], []);

  const settimaneComplete = data.filter(r => r.preparato && r.prelevato).length;
  const daPrelevare = data
    .filter(r => r.preparato && !r.prelevato)
    .reduce((s, r) => s + (r.importo_preparato !== null && r.importo_preparato !== undefined ? Number(r.importo_preparato) : (Number(r.totale_contanti) || 0)), 0);
  const totaleContanti = data.reduce((s, r) => s + (Number(r.totale_contanti) || 0), 0);

  const StatDaPrelevare = daPrelevare > 0 ? StatWarn : StatSuccess;
  const StatSettimane = settimaneComplete === data.length && data.length > 0 ? StatDone : StatCard;

  return (
    <PageContainer>
      <PageHeader
        title="Versamento Cassa"
        subtitle="Contanti da versare, organizzati per settimana (lunedì → domenica). Una volta segnato, il flag non può essere rimosso."
      />

      {/* Cards riepilogo */}
      <StatGrid>
        <StatSuccess
          value={`€ ${totaleContanti.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          label="Totale contanti (periodo)"
        />
        <StatDaPrelevare
          value={`€ ${daPrelevare.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          label="Preparati ma non prelevati"
        />
        <StatSettimane
          value={`${settimaneComplete} / ${data.length}`}
          label="Settimane completate"
        />
      </StatGrid>

      {/* Griglia AG Grid */}
      {error && <Alert $variant="error">{error}</Alert>}
      <GelatoDataGrid
        rowData={data}
        columnDefs={colsVersamenti}
        height="560px"
        loading={loading}
        pagination={false}
        context={ctxRef.current}
        onGridReady={e => { gridApiRef.current = e.api; }}
        getRowStyle={(p: any) => p.data?.preparato && p.data?.prelevato ? { background: 'var(--success-soft)' } : undefined}
        exportable={true}
        csvFileName="versamenti-cassa"
      />
    </PageContainer>
  );
};

export default VersamentoCassaPage;
